// Browser-local diagnostics only: durations/counts, never patient/account data.
// Resource Timing only exposes completed network requests. Track the API load
// lifecycle as well, including work that was already in flight at render start.
let patientApiLoads = [];
const clockNow = () => window.performance?.now?.() || 0;

export function beginPatientApiLoad(userId) {
  const load = { userId, startedAt: clockNow(), finishedAt: null, outcome: 'pending' };
  patientApiLoads.push(load);
  return (outcome) => {
    if (load.finishedAt !== null) return;
    load.finishedAt = clockNow();
    load.outcome = outcome;
    const completed = patientApiLoads.filter(item => item.finishedAt !== null);
    const retained = new Set(completed.slice(-50));
    patientApiLoads = patientApiLoads.filter(item => item.finishedAt === null || retained.has(item));
  };
}

export function clearPatientApiTimings(userId) {
  patientApiLoads = userId ? patientApiLoads.filter(item => item.userId !== userId) : [];
}

// Starts at React render initialization, not the navigation click or commit.
// Page FCP is a separate measurement of the original document navigation.
export function startDashboardTiming({ userId } = {}) {
  const clock = window.performance;
  const startedAt = clock?.now?.() || 0;
  const stages = {};
  let reported = false;
  return {
    stage(name) { if (!(name in stages)) stages[name] = (clock?.now?.() || 0) - startedAt; },
    report({ patientCount, outcome, isRefreshing = false }) {
      if (reported || !clock?.getEntriesByType) return;
      reported = true;
      const now = clock.now();
      const navigation = clock.getEntriesByType('navigation')[0];
      const paint = clock.getEntriesByType('paint').find(entry => entry.name === 'first-contentful-paint');
      const overlappingLoads = patientApiLoads.filter(load => load.userId === userId
        && load.startedAt <= now && (load.finishedAt === null || load.finishedAt >= startedAt));
      const round = value => Number.isFinite(value) ? Math.round(value) : null;
      console.debug('[dashboard-performance]', JSON.stringify({
        schemaVersion: 2,
        measurementScope: 'dashboard-render-to-first-content',
        pageNavigationType: navigation?.type || null,
        pageNavigationFcpMs: round(paint?.startTime),
        dashboardRenderStartedAtMs: round(startedAt),
        accountSnapshotReadyMs: round(stages.accountReady),
        shellReadyMs: round(stages.shellReady),
        renderToContentEstimateMs: round(now - startedAt),
        pageElapsedAtContentEstimateMs: round(now),
        patientApiLoadsOverlappingRender: overlappingLoads.length,
        patientApiLoadsStartedBeforeRender: overlappingLoads.filter(load => load.startedAt < startedAt).length,
        patientApiLoadsInFlight: overlappingLoads.filter(load => load.finishedAt === null).length,
        // Includes token/header preparation and JSON decoding, not just wire time.
        patientApiLoadDurationsMs: overlappingLoads.filter(load => load.finishedAt !== null).map(load => round(load.finishedAt - load.startedAt)),
        patientApiLoadFailures: overlappingLoads.filter(load => load.outcome === 'error').length,
        patientDataState: outcome === 'error' ? 'error' : isRefreshing ? 'cached-refreshing' : 'settled',
        patientCount, outcome,
      }));
    },
  };
}

export function afterDashboardPaint(callback) {
  let second;
  const first = window.requestAnimationFrame(() => {
    second = window.requestAnimationFrame(callback);
  });
  return () => {
    window.cancelAnimationFrame(first);
    if (second !== undefined) window.cancelAnimationFrame(second);
  };
}
